import React from 'react';
import PropTypes from 'prop-types';
import InteractionOutcomeIndicator from '../../interactionOutcome/InteractionOutcomeIndicator';
import KeyboardHelpAndPrompt from '../../keyboardHelp/KeyboardHelpAndPrompt';
import constants from '../../../../qti/Constants';
import LoadTracker from '../../../../services/LoadTracker';
import HotSpotService from '../../../../services/questionView/HotSpotService';
import ImageDisplay from './../../media/image/ImageDisplay';
import HotSpotArea from './HotSpotArea';

import styles from './hotSpotInteraction.scss';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../../../services/a11yIIBScreenReaderService';
import { t } from '../../../../services/i18nService';

const EXTRA_RESPONSE_IDENTIFIER = constants.extraResponseIdentifiers.HOTSPOT_INTERACTION.pointer;
const keys = constants.keyCodes;
const pointerImageModel = {
    // eslint-disable-next-line max-len
    url: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAQAAAAngNWGAAAACXBIWXMAAAsTAAALEwEAmpwYAAADGGlDQ1BQaG90b3Nob3AgSUNDIHByb2ZpbGUAAHjaY2BgnuDo4uTKJMDAUFBUUuQe5BgZERmlwH6egY2BmYGBgYGBITG5uMAxIMCHgYGBIS8/L5UBFTAyMHy7xsDIwMDAcFnX0cXJlYE0wJpcUFTCwMBwgIGBwSgltTiZgYHhCwMDQ3p5SUEJAwNjDAMDg0hSdkEJAwNjAQMDg0h2SJAzAwNjCwMDE09JakUJAwMDg3N+QWVRZnpGiYKhpaWlgmNKflKqQnBlcUlqbrGCZ15yflFBflFiSWoKAwMD1A4GBgYGXpf8EgX3xMw8BSMDVQYqg4jIKAUICxE+CDEESC4tKoMHJQODAIMCgwGDA0MAQyJDPcMChqMMbxjFGV0YSxlXMN5jEmMKYprAdIFZmDmSeSHzGxZLlg6WW6x6rK2s99gs2aaxfWMPZ9/NocTRxfGFM5HzApcj1xZuTe4FPFI8U3mFeCfxCfNN45fhXyygI7BD0FXwilCq0A/hXhEVkb2i4aJfxCaJG4lfkaiQlJM8JpUvLS19QqZMVl32llyfvIv8H4WtioVKekpvldeqFKiaqP5UO6jepRGqqaT5QeuA9iSdVF0rPUG9V/pHDBYY1hrFGNuayJsym740u2C+02KJ5QSrOutcmzjbQDtXe2sHY0cdJzVnJRcFV3k3BXdlD3VPXS8Tbxsfd99gvwT//ID6wIlBS4N3hVwMfRnOFCEXaRUVEV0RMzN2T9yDBLZE3aSw5IaUNak30zkyLDIzs+ZmX8xlz7PPryjYVPiuWLskq3RV2ZsK/cqSql01jLVedVPrHzbqNdU0n22VaytsP9op3VXUfbpXta+x/+5Em0mzJ/+dGj/t8AyNmf2zvs9JmHt6vvmCpYtEFrcu+bYsc/m9lSGrTq9xWbtvveWGbZtMNm/ZarJt+w6rnft3u+45uy9s/4ODOYd+Hmk/Jn58xUnrU+fOJJ/9dX7SRe1LR68kXv13fc5Nm1t379TfU75/4mHeY7En+59lvhB5efB1/lv5dxc+NH0y/fzq64Lv4T8Ffp360/rP8f9/AA0ADzT6lvFdAAAAIGNIUk0AAHolAACAgwAA+f8AAIDpAAB1MAAA6mAAADqYAAAXb5JfxUYAAABBSURBVHja7JExCgAgDAMv4v+/HDfBVkFdNWN6IdDIBFmAQaNd2NSbYMWLS/DF9OPydbWcssq9QI2bduZvfQK2AQD2gAwlzMzSwwAAAABJRU5ErkJggg==',
};

class HotSpotInteraction extends React.Component {
    static propTypes = {
        callback: PropTypes.func.isRequired,
        extraResponses: PropTypes.array,
        handleInteractionReady: PropTypes.func.isRequired,
        isReadOnly: PropTypes.bool,
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        userResponse: PropTypes.array.isRequired, // array with simpleChoice identifiers
        correctResponses: PropTypes.array,
        services: PropTypes.object,
        settingValues: PropTypes.object,
        direction: PropTypes.string,
    };

    state = {
        stepSize: 1,
        useKeyboard: false,
    };

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillMount() {
        this.setMediaTracker();
        this.trackBackgroundImage();
        this.trackPointerImage();
    }

    componentDidMount() {
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleKeyUp = this.handleKeyUp.bind(this);
        this.canvas.addEventListener('keydown', this.handleKeyDown);
        this.canvas.addEventListener('keyup', this.handleKeyUp);
        if (this.shouldFocusOnPointer()) {
            this.pointer.focus();
        }
        this.mediaTracker.setAllItemsAdded();
    }

    componentDidUpdate() {
        if (this.shouldFocusOnPointer()) {
            this.pointer.focus();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('keyup', this.handleKeyUp);
    }

    setMediaTracker = () => {
        this.mediaTracker = new LoadTracker(
            this.props.handleInteractionReady,
            `Interaction ${this.props.model.identifier}/images`
        );
    };

    trackBackgroundImage = () => {
        const { backgroundImageModel } = this.props.model;

        if (backgroundImageModel && backgroundImageModel.url) {
            this.onBackgroundImageReady = this.mediaTracker.addItemToTrack(`Media ${backgroundImageModel.url}`);
        }
    };

    trackPointerImage = () => {
        const { model, extraResponses } = this.props;

        if (HotSpotService.showPointer(model, extraResponses, this.state.useKeyboard)) {
            this.onPointerImageReady = this.mediaTracker.addItemToTrack('Pointer hotspot');
        }
    };

    handleClick = (e) => {
        if (this.props.isReadOnly) {
            return;
        }

        const canvas = this.canvas.getBoundingClientRect();
        const eventX = e.clientX - canvas.left;
        const eventY = e.clientY - canvas.top;

        this.handleUserResponse(eventX, eventY);
    };

    handleUserResponse = (clickEventX, clickEventY, isFromKeyboardArrows = false) => {
        const { model, userResponse, extraResponses } = this.props;
        let currentUserResponse = [];
        const pointerExtraResponse = [{
            identifier: EXTRA_RESPONSE_IDENTIFIER,
            candidateResponse: [],
        }];

        if (model.cardinality === 'multiple') {
            // preserving the response from previous selection
            currentUserResponse = userResponse.slice();
            pointerExtraResponse[0].candidateResponse = extraResponses[0][EXTRA_RESPONSE_IDENTIFIER].slice();
        }

        const currentExtraResponses = pointerExtraResponse[0].candidateResponse;
        const pointerStoredAtIndex = currentExtraResponses.findIndex((pair) => {
            return HotSpotService.isPointerStored(clickEventX, clickEventY, pair);
        });
        const selectedHotSpotIndex = model.hotSpotAreas.findIndex((hotSpotArea) => {
            return HotSpotService.isPointerInHotSpot(clickEventX, clickEventY, hotSpotArea);
        });

        // User Response handling
        if (selectedHotSpotIndex !== -1) {
            // clicked inside a HotSpot
            const selectedHotSpotIdentifier = model.hotSpotAreas[selectedHotSpotIndex].identifier;
            const hotSpotStoredIndex = currentUserResponse.indexOf(selectedHotSpotIdentifier);
            const shouldUnselectHotSpot = (model.markHotspots && hotSpotStoredIndex !== -1)
                || (!model.markHotspots && pointerStoredAtIndex !== -1);

            if (shouldUnselectHotSpot) {
                // delete identifier
                HotSpotService.deleteResponseAtIndex(currentUserResponse, hotSpotStoredIndex);
            } else {
                HotSpotService.addResponse(model, currentUserResponse, selectedHotSpotIdentifier);
            }
        } else if (!model.markHotspots && !model.regardUnansweredOutsideHotspot) {
            // IA1-5874 - using the index of the coordinates from the extra response handling array to remove the
            // correct wrong choice identifier in the user response handling array, else if the wrong choice does
            // not exist, a wrong choice identifier is added to the user response handling array.
            if (pointerStoredAtIndex !== -1) {
                HotSpotService.deleteResponseAtIndex(currentUserResponse, pointerStoredAtIndex);
            } else {
                HotSpotService.addResponse(model, currentUserResponse, HotSpotService.getDefaultWrongChoiceIdentifier());
            }
        }

        // Extra Response handling
        const clickEventXYCoords = `${parseInt(clickEventX, 10)} ${parseInt(clickEventY, 10)}`;
        if (!model.markHotspots && pointerStoredAtIndex !== -1) {
            if (isFromKeyboardArrows) {
                // pointer is shifting position, we update its coords
                HotSpotService.replaceResponseAtIndex(currentExtraResponses, pointerStoredAtIndex, clickEventXYCoords);
            } else {
                // unselect/remove pointers
                HotSpotService.deleteResponseAtIndex(currentExtraResponses, pointerStoredAtIndex);
            }
        } else {
            HotSpotService.addResponse(model, currentExtraResponses, clickEventXYCoords);
        }

        // store the response
        this.props.callback(model.identifier, currentUserResponse, pointerExtraResponse);
    };

    handleKeyDown = (e) => {
        // Handle Enter or Space key to place pointer in center
        if (a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v3) &&
            !this.props.isReadOnly &&
            (e.keyCode === constants.allowedKeyCodesNumericInput.ENTER || e.keyCode === keys.SPACE)
        ) {
            e.preventDefault();

            // Calculate center coordinates of the canvas
            const centerX = Math.floor(this.canvas.clientWidth / 2);
            const centerY = Math.floor(this.canvas.clientHeight / 2);

            // Place pointer at center
            this.handleUserResponse(centerX, centerY);

            return false;
        }

        if (!this.props.isReadOnly
            && this.state.useKeyboard
            && (e.keyCode >= keys.LEFT && e.keyCode <= keys.DOWN)) {
            e.preventDefault();

            return this.handleArrowKeys(e.keyCode, e.shiftKey);
        }

        const newStepSize = constants.keyCodeStepSizeMap[e.keyCode];
        if (newStepSize) {
            this.setState({ stepSize: newStepSize });
        }

        return true;
    };

    handleArrowKeys = (keyCode, shiftKeyPressed) => {
        const stepSize = shiftKeyPressed ? 10 : this.state.stepSize;
        const currentTop = parseInt(this.pointer.offsetTop || 0, 10);
        const currentLeft = parseInt(this.pointer.offsetLeft || 0, 10);
        const height = parseInt(this.canvas.clientHeight, 10);
        const width = parseInt(this.canvas.clientWidth, 10);

        if (keyCode === keys.DOWN) {
            let newTop = currentTop + stepSize;
            if (newTop > height - 15) {
                newTop = height - 15;
            }
            this.pointer.style.top = `${newTop}px`;
        } else if (keyCode === keys.LEFT) {
            let newLeft = currentLeft - stepSize;
            if (newLeft < -15) {
                newLeft = -15;
            }
            this.pointer.style.left = `${newLeft}px`;
        } else if (keyCode === keys.RIGHT) {
            let newLeft = currentLeft + stepSize;
            if (newLeft > width - 15) {
                newLeft = width - 15;
            }
            this.pointer.style.left = `${newLeft}px`;
        } else if (keyCode === keys.UP) {
            let newTop = currentTop - stepSize;
            if (newTop < -15) {
                newTop = -15;
            }
            this.pointer.style.top = `${newTop}px`;
        }

        return false;
    };

    handleKeyUp = (e) => {
        if (!this.state.useKeyboard || !constants.arrowKeyCodes[e.keyCode]) {
            return;
        }
        const y = parseInt(this.pointer.style.top || 0, 10) + 15;
        const x = parseInt(this.pointer.style.left || 0, 10) + 15;

        this.handleUserResponse(x, y, true);
    };

    toggleKeyboardNavigation = (useKeyboard) => {
        this.setState({ useKeyboard });
    };

    shouldFocusOnPointer = () => {
        const { isReadOnly, model, extraResponses } = this.props;

        return !isReadOnly
            && HotSpotService.showPointer(model, extraResponses, this.state.useKeyboard)
            && this.pointer
            && document.activeElement !== this.pointer
            && this.state.useKeyboard;
    };

    onFocus = (e) => {
        this.toggleKeyboardNavigation(true);
        this.pointer = e.currentTarget;
    };

    onPointerClick = () => {
        if (this.props.isReadOnly) {
            return;
        }

        this.handleUserResponse(this.pointer.offsetLeft + 15, this.pointer.offsetTop + 15);
    };

    onBlur = () => {
        this.toggleKeyboardNavigation(false);
    };

    onKeyDown = (e) => {
        if (a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v3)) {
            return this.handleKeyDown(e);
        }

        return undefined;
    }

    render() {
        const {
            model,
            direction,
            userResponse,
            extraResponses,
            correctResponses,
            isReadOnly,
            services,
            settingValues = {},
        } = this.props;
        const responseCoordinates = HotSpotService.getResponseCoordinates(model, extraResponses);

        const {
            backgroundImageModel,
            markHotspots,
        } = model;
        const className = `${styles.hotSpotInteraction} interaction`;
        const showCorrectResponses = !!correctResponses;
        const hasBackgroundImage = backgroundImageModel && backgroundImageModel.url;
        let hotSpotAreas;
        if (markHotspots || showCorrectResponses) {
            hotSpotAreas = model.hotSpotAreas.map((hotSpot) => {
                const isAttempted = HotSpotService.getIsAttempted(userResponse, hotSpot.identifier);
                let isHotSpotSelected;

                if (model.cardinality === 'multiple') {
                    isHotSpotSelected = userResponse.includes(hotSpot.identifier);
                } else {
                    isHotSpotSelected = (userResponse[0] === hotSpot.identifier);
                }

                return (
                    <HotSpotArea
                        hotSpot={hotSpot}
                        selected={isHotSpotSelected}
                        key={hotSpot.identifier}
                        model={model}
                    >
                        {
                            showCorrectResponses ?
                                <InteractionOutcomeIndicator
                                    addItemToTrack={this.mediaTracker.addItemToTrack}
                                    answerOption={hotSpot.identifier}
                                    correctResponses={correctResponses}
                                    isAttempted={isAttempted}
                                    skipUnansweredIndicator
                                    translationService={services.translationService}
                                /> : null
                        }
                    </HotSpotArea>
                );
            });
        }

        const canvasStyle = {
            height: `${model.interactionHeight}px`,
        };

        const showPointer = HotSpotService.showPointer(model, extraResponses, this.state.useKeyboard);
        let pointerList = null;

        const isA11yIterationV3Supported = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v3);
        const tabIndexValue = isA11yIterationV3Supported ? 0 : undefined;
        const captionKeyForKeyboardHelpA11Y = isA11yIterationV3Supported
            ? t('interactions.hotSpot.keyboardInteractionHelpTextForA11Y')
            : t('interactions.hotSpot.keyboardInteractionHelpText');
        const ariaLabelValue = isA11yIterationV3Supported ? t('interactions.hotSpot.canvas') : null;
        if (showPointer || !isReadOnly) {
            pointerList = responseCoordinates.map((pair) => {
                const pairXY = pair.split(' ');
                const pointerDivStyle = pair.length > 0
                    ? { left: (parseInt(pairXY[0], 10) - 15), top: (parseInt(pairXY[1], 10) - 15) }
                    : { left: 'calc(50% - 15px)', top: 'calc(50% - 15px)' };

                return (
                    <div // eslint-disable-line jsx-a11y/no-static-element-interactions
                        className={styles.pointer}
                        data-type="pointer"
                        key={pair}
                        style={pointerDivStyle}
                        tabIndex="0" // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
                        ref={(element) => {
                            this.pointer = element;
                        }}
                        onFocus={this.onFocus}
                        onBlur={this.onBlur}
                        onClick={this.onPointerClick}
                    >
                        {
                            showPointer ?
                                <ImageDisplay
                                    model={pointerImageModel}
                                    reportReady={this.onPointerImageReady}
                                    autoScale
                                /> : null
                        }

                        {
                            showCorrectResponses && !markHotspots && !HotSpotService.getPointerAnswerOption(userResponse) ?
                                <InteractionOutcomeIndicator
                                    addItemToTrack={this.mediaTracker.addItemToTrack}
                                    answerOption={HotSpotService.getPointerAnswerOption(userResponse)}
                                    isAttempted={HotSpotService.getPointerIsAttempted(model, extraResponses)}
                                    correctResponses={correctResponses}
                                    translationService={services.translationService}
                                /> : null
                        }
                    </div>
                );
            });
        }

        return (
            <div className={className} dir={direction}>
                <KeyboardHelpAndPrompt
                    captionKey={captionKeyForKeyboardHelpA11Y}
                    translationService={services.translationService}
                    model={model}
                    showAccessibilityHelpers={settingValues.show_accessibility_helpers && !isReadOnly}
                    tabIndex={tabIndexValue}
                />
                <div // eslint-disable-line jsx-a11y/no-static-element-interactions
                    className={styles.canvas + (model.markHotspots ? ' marked' : ' unmarked')}
                    style={canvasStyle}
                    onClick={this.handleClick}
                    ref={(elm) => { this.canvas = elm; }}
                    tabIndex={tabIndexValue}
                    onKeyDown={this.onKeyDown}
                    aria-label={ariaLabelValue}
                >
                    {
                        hasBackgroundImage ?
                            <div
                                className={styles.backgroundImageWrapper}
                                style={canvasStyle}
                            >
                                <ImageDisplay
                                    model={backgroundImageModel}
                                    reportReady={this.onBackgroundImageReady}
                                    autoScale
                                />
                            </div> : null
                    }

                    { markHotspots || showCorrectResponses ? hotSpotAreas : null }
                    { showPointer || !isReadOnly ? pointerList : null }
                </div>
            </div>
        );
    }
}

export default HotSpotInteraction;
